import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'

import { useIncidentAudio } from './../../features/account-information/hooks/useIncidentAudio'
import { useFilterNotification } from './useFilterNotification'
import { useIncidentsBroadCastChannel } from './useIncidentsBroadCastChannel'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useDispatch, useSelector } from 'react-redux'

import { ApolloError, useApolloClient, useSubscription } from '@apollo/client'

import { GraphqlClientType } from '@/config/apollo'
import {
  NotificationType,
  ON_UPDATE_NOTIFICATION_COUNT,
  OnUpdateNotificationCountSubscription,
} from '@/graphql/appsync-notifications'
import { IncidentPriority } from '@/graphql/generated/schemas'
import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import {
  selectAreNotificationsMuted,
  selectNotifications,
  setNotificationCount,
  setNotifications,
} from '@/redux/notifications/notificationsSlice'
import { trackEvent } from '@/utils/analytics'

import { createSubscription } from './subscription'
import { NotificationDataIProps, UseOnNotificationIProps } from './types'
import { isUniqueNotification } from './utils'

export const NotificationsContext = createContext<UseOnNotificationIProps>(null)

export const NotificationsProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const dispatch = useDispatch()
  const notifications = useSelector(selectNotifications)
  const areNotificationsMuted = useSelector(selectAreNotificationsMuted)
  const me = useAppSelector(selectMe)
  const isCriticalBypassOn = useFeatureFlag(FeatureFlag.priorityQueue)

  const { shouldShowNotification } = useFilterNotification()
  const { postIncidentsMessage } = useIncidentsBroadCastChannel()

  const client = useApolloClient()
  const [shouldReconnect, setShouldReconnect] = useState(true)
  const [error, setError] = useState<ApolloError | undefined>(undefined)
  const [data, setData] = useState<NotificationDataIProps | undefined>(
    undefined
  )

  useEffect(() => {
    if (me?.id) {
      const subscription = createSubscription({
        client: client,
        userId: me.id,
        setData,
        setError,
        setShouldReconnect,
      })
      return () => subscription.unsubscribe()
    }
  }, [shouldReconnect, me?.id])

  const incidentAudio = useIncidentAudio()

  /**
   * @return boolean if a sound was played or not. Depends on user settings, feature flag, and actually playing (browser permissions)
   */
  const playAlertIfApplicable = async (
    priority?: IncidentPriority | null,
    notificationType?: NotificationType
  ): Promise<boolean> => {
    const didPlaySound: boolean = await incidentAudio.playAlertSound(
      priority,
      notificationType
    )

    if (didPlaySound) {
      trackEvent(`Played Incident Audio Alert`, {
        incidentId: data.notification?.incidentId,
        incidentDisplayId: data.notification?.incidentDisplayId,
        incident_priority: priority ?? null,
      })
    }

    return didPlaySound
  }

  useEffect(() => {
    const handleNotification = async () => {
      if (data?.notification) {
        // Add new notification. Check if unique to prevent dups
        if (isUniqueNotification(notifications, data)) {
          // Always refresh incidents table and global count, regardless of mute state
          client.refetchQueries({ include: ['GlobalUnresolvedIncidentsCount'] })
          // Broadcast out each time we have a unique Incident come in
          postIncidentsMessage(data)

          let shouldWeShow = false
          let didPlaySound = false

          const priority = data.notification.incidentPriority
          const notificationType = data.notification.notificationType
          // Priority belongs to the incident, not the event, so a Critical incident's
          // clear/flag/audio-message notifications carry the same priority as its
          // arrival — restrict the bypass to the new-incident event itself, or every
          // later event on that incident would also bypass mute and the table filter.
          const isCriticalBypass =
            priority === IncidentPriority.Critical &&
            notificationType === NotificationType.IncidentNew &&
            isCriticalBypassOn

          // Only show notification UI/audio when not muted (unless Critical bypass applies)
          if (!areNotificationsMuted || isCriticalBypass) {
            shouldWeShow =
              isCriticalBypass ||
              (await shouldShowNotification(data.notification.incidentId))

            if (shouldWeShow) {
              dispatch(setNotifications([...notifications, data?.notification]))
              didPlaySound = await playAlertIfApplicable(
                priority,
                notificationType
              )
            }
          }

          // A new-incident toast MAY have been shown - let's track this
          trackEvent(`Received New Incident Notification`, {
            incidentId: data.notification.incidentId, // Incident UUID. Can get interesting insights into TTR after showing a toast, etc
            notifications_are_muted: areNotificationsMuted, // If bottom right bell/mute icon is clicked
            tried_to_show: shouldWeShow, // If this Incident matches whatever filter?
            did_play_sound: didPlaySound, // If we played an audio alert (can be true while muted, for a new Critical-priority incident bypass)
            type: data.notification.notificationType,
            incident_priority: priority ?? null,
          })
        }
      }
    }
    handleNotification()
  }, [data])

  useSubscription<OnUpdateNotificationCountSubscription>(
    ON_UPDATE_NOTIFICATION_COUNT,
    {
      variables: { userId: me?.id },
      skip: !me?.id,
      onSubscriptionData: ({ subscriptionData }) => {
        dispatch(
          setNotificationCount(
            subscriptionData?.data?.onUpdateNotificationCount.notificationCount
          )
        )
      },
      context: { target: GraphqlClientType.APPSYNC_NOTIFICATIONS },
    }
  )

  return (
    <NotificationsContext.Provider value={{ data, error }}>
      {children}
    </NotificationsContext.Provider>
  )
}

export const useOnNotification = () => useContext(NotificationsContext)
